import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { filter, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { AppInjector } from 'src/app/app-injector.module';
import { AppContext } from 'src/app/app/context';
import { AppUserWrapper } from 'src/app/app/user';
import { ContentDatasetDto, ContentSelectorApiService, ContentSelectorDashboardDto, ContentSelectorItemDto, ContentSelectorVisualDto, DataDateFormat, DataTimeFormat, SchemaType, VisualService } from 'src/app/services/api.services';
import { getInsightsPbiChartType } from 'src/app/shared/chart/chart';
import { getProductDescriptor, getTitleForKnownProduct } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { DataModel, DataSchema } from '../../dataModel';
import { DataModelResolver } from '../../dataModel/DataModelResolver';
import { DataType } from '../../dataModel/DataType';
import { DataFormatter } from '../../formatting/DataFormatter';
import { FilterUtils } from '../../utils/FilterUtils';
import { VisualFieldTargetResolver } from '../../visuals/VisualFieldTargetResolver';
import { ContentSelectorDialogComponent } from './ContentSelectorDialog.component';
import { ContentSelectorDashboardPreviewComponent, ContentSelectorVisualPreviewComponent } from './previews';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';
import { user } from '../../../admin/spend/state/pivot-import/selectors';

@Injectable({ providedIn: 'root' })
export class ContentSelectorDialog {
  readonly dialog = inject(MatDialog);
  readonly currencyService = inject(AppCurrencyService);

  open<T>(config: ContentSelectorDialog.Config<T>, options: MatDialogConfig<ContentSelectorDialog.Data<T>>) {
    return this.currencyService.load().pipe(
      map(currencies => this.dialog.open(
        ContentSelectorDialogComponent,
        {
          ...ContentSelectorDialog.defaultConfig,
          data: {
            config,
            data: {
              ...options?.data,
              context: {
                currencies,
                ...options?.data?.context,
              }
            }
          }
        }) as MatDialogRef<ContentSelectorDialogComponent<ContentSelectorDialog.Data<T>>, ContentSelectorDialog.Result<T>>
      )
    );
  }

  openDashboards(options: MatDialogConfig<ContentSelectorDialog.Data<ContentSelectorDashboardDto>>) {
    return this.open<ContentSelectorDashboardDto>(ContentSelectorDialog.Config.dashboards, options);
  }

  openVisuals(options: MatDialogConfig<ContentSelectorDialog.Data<ContentSelectorVisualDto>>) {
    return this.open<ContentSelectorVisualDto>(ContentSelectorDialog.Config.visuals, options);
  }

  static readonly defaultConfig: MatDialogConfig<any> = {
    minWidth: '1200px',
    maxWidth: '95vw',
    width: '100%',
    minHeight: '600px',
    closeOnNavigation: true,
    disableClose: true,
    hasBackdrop: true,
    backdropClass: 'content-selector-dialog-backdrop',
    panelClass: 'content-selector-dialog-panel',
  };
}

export namespace ContentSelectorDialog {
  export interface ItemIcon {
    readonly default: string;
    readonly selected: string;
    readonly title: string;
  }

  export class Item<T> {
    readonly selected = signal(false);

    readonly category = signal<ItemCategory<T>>(null);
    readonly groups = signal<ReadonlyArray<ItemGroup<T>>>([]);
    _groups: ItemGroup<T>[] = [];

    readonly viewed = signal(false);


    readonly hideByFavouritesOnly = signal(false);
    readonly hideByFiltering = signal(false);
    readonly visible = computed(() => !this.hideByFavouritesOnly() && !this.hideByFiltering());


    readonly draw = computed(() => this.groups().some(g => g.selected()));

    readonly key: string;
    readonly data: T;
    readonly categoryId: ItemCategoryId;
    readonly title: string;
    readonly icon?: ItemIcon;
    readonly groupNames: ReadonlyArray<ItemGroupName>;
    readonly favourite: WritableSignal<boolean>;

    constructor(
      readonly id: any,
      init: {
        readonly data: T;
        readonly categoryId: ItemCategoryId;
        readonly title: string;
        readonly icon: ItemIcon;
        readonly groupNames: ReadonlyArray<ItemGroupName>;
        readonly favourite?: boolean;
      }) {
      this.key = `${init.categoryId}||${this.id}`;
      this.data = init.data;
      this.categoryId = init.categoryId;
      this.title = init.title;
      this.icon = init.icon;
      this.groupNames = init.groupNames;
      this.favourite = signal(!!init.favourite); 
    }

    init() {
      this.groups.set(this._groups);
    }
  }


  export type ItemGroupName = { title: string; order: number };

  export class ItemGroup<T> {
    readonly selected = signal(false);

    readonly items = signal<ReadonlyArray<Item<T>>>([]);
    _items: Item<T>[] = [];


    readonly visibleItems = computed(() => this.items().filter(x => x.visible()));

    readonly draw = computed(() => this.category.selected() && this.visibleItems().length > 0);

    constructor(
      readonly category: ItemCategory<T>,
      readonly id: string,
      readonly name: string,
      readonly order: number,
      readonly all?: boolean) { }

    init() {
      this._items.sort((a, b) => {
        let d = (a.favourite ? -1 : 0) - (b.favourite ? -1 : 0);
        if (d == 0)
          d = a.title.localeCompare(b.title);

        return d;
      });

      this.items.set(this._items);
    }
  }

  
  export type ItemCategoryId = string;

  export class ItemCategory<T> {
    readonly selected = signal(false);

    readonly groups = signal<ReadonlyArray<ItemGroup<T>>>([]);
    
    _items: Item<T>[] = [];

    readonly visibleItemGroups = computed(() => this.groups().filter(g => g.visibleItems().length > 0));
    readonly visibleItems = computed(() =>
      this.visibleItemGroups()
        .filter(x => x.all)
        .reduce((n, x) => n + x.visibleItems().length, 0));

    readonly minWidthStyle = computed(() => `${this.minWidth}px`);
    
    constructor(
      readonly id: ItemCategoryId,
      readonly title: string,
      readonly minWidth: number) { }

    init() {
      let allGroup = new ItemGroup<T>(this, `${this.id}.All`, 'All', -1, true);
      let othersGroup = new ItemGroup<T>(this, `${this.id}.Other`, 'Other', Number.MAX_SAFE_INTEGER);

      let groups: ItemGroup<T>[] = [];

      for (let i of this._items) {
        i._groups.push(allGroup);
        allGroup._items.push(i);

        for (let g of i.groupNames) {
          let group = groups.find(x => x.name == g.title);
          if (!group) {
            group = new ItemGroup<T>(this, `${this.id}.${g.title}`, g.title, g.order);
            groups.push(group);
          }

          i._groups.push(group);
          group._items.push(i);
        }

        if (!i._groups.length) {
          i._groups.push(othersGroup);
          othersGroup._items.push(i);
        }
      }

      groups.sort((a, b) => {
        let d = a.order - b.order;
        if (d === 0)
          d = a.name.localeCompare(b.name);

        return d;
      });

      groups = [
        allGroup,
        ...groups,
        othersGroup
      ];

      groups.forEach(g => g.init());
      
      this.groups.set(groups);
    }
  }

  export namespace ItemCategory {
    export const 
      library: ItemCategoryId = 'library',
      user:    ItemCategoryId = 'user',
      shared:  ItemCategoryId = 'shared';
  }


  export interface SourceConfig {
    readonly model: DataModel;
    readonly dataFormatter: DataFormatter;
  }


  export class Config<T> {
    constructor(
      readonly categories: () => ReadonlyArray<ItemCategory<T>>,
      readonly source: (config: SourceConfig) => Observable<ReadonlyArray<Item<T>>>,
      readonly options?: {
        readonly itemPreview?: any;
      }) { }
  }


  interface ConfigContext {
    readonly appContext: AppContext;
    readonly user: AppUserWrapper;
    readonly dataFormatter: DataFormatter;
  }

  export namespace Config {
    function *getItemCategories({ user }: ConfigContext, d: ContentSelectorItemDto): Iterable<ItemCategoryId> {
      if (d.fromLibrary)
        yield ItemCategory.library;
      else
      if (d.ownerId == user.id || (d.ownerName && d.ownerName?.toLowerCase() == user.username?.toLowerCase()))
        yield ItemCategory.user;

      if (d.shared)
        yield ItemCategory.shared;
    }

    export const dashboards = new Config<ContentSelectorDashboardDto>(() => [
      new ItemCategory<ContentSelectorDashboardDto>(ItemCategory.library, 'Library', 130),
      new ItemCategory<ContentSelectorDashboardDto>(ItemCategory.user, 'My Dashboards', 210),
      new ItemCategory<ContentSelectorDashboardDto>(ItemCategory.shared, 'Shared Dashboards', 230),
    ], config => {
      let api = AppInjector.get(ContentSelectorApiService);
      let appContext = AppInjector.get(AppContext);
      
      let context: ConfigContext = {
        appContext,
        user: appContext.user(),
        ...config,
      };

      return api.listDashboards(true, true, false).pipe(
        map(d => d.map(d => {
          let categoryIds = [...getItemCategories(context, d)];
          if (!categoryIds.length)
            return null;

          let groups = d.categories.map((x, i) => ({ title: x.name, order: 0 }));

          return categoryIds.map(categoryId => new Item<ContentSelectorDashboardDto>(d.id, {
            data: d,
            categoryId,
            title: d.name,
            icon: null,
            groupNames: groups,
            favourite: d.favourite
          }));
        })),
        map(x => x.flat()),
        filter(x => !!x)
      );
    }, {
      itemPreview: ContentSelectorDashboardPreviewComponent,
    });

    export const visuals = new Config<ContentSelectorVisualDto>(() => [
      new ItemCategory<ContentSelectorVisualDto>(ItemCategory.library, 'Library', 130),
      new ItemCategory<ContentSelectorVisualDto>(ItemCategory.user,    'My Visuals', 170),
      new ItemCategory<ContentSelectorVisualDto>(ItemCategory.shared,  'Shared Visuals', 190),
    ], config => {
      let api = AppInjector.get(ContentSelectorApiService);
      let appContext = AppInjector.get(AppContext);

      let context: ConfigContext = {
        appContext,
        user: appContext.user(),
        ...config,
      };

      return api.listVisuals(true, true, false).pipe(
        map(x => x.map(v => {
          let chartType = getInsightsPbiChartType(v.visual.type);
          let categoryIds = [...getItemCategories(context, v)];
          if (!categoryIds.length)
            return null;

          let groups = v.categories.map((x, i) => ({ title: x.name, order: 0 }));

          if (!groups.length && !v.fromLibrary) {
            if (SchemaUtils.isCore(v.visual.schema)) {
              let product = getProductDescriptor(v.visual.schema.product);
              if (product)
                groups.push({ title: product.title, order: 9999 + product.order });
            }
            else if (SchemaUtils.isUserDataset(v.visual.schema)) {
              groups.push({ title: 'Extended Datasets', order: Number.MAX_SAFE_INTEGER - 1 });
            }
          }

          return categoryIds.map(categoryId => new Item<ContentSelectorVisualDto>(v.id, {
            data: v,
            categoryId,
            title: v.name,
            icon: {
              default:  chartType.previewImageUrl,
              selected: chartType.previewWhiteImageUrl, 
              title:    chartType.title 
            },
            groupNames: groups,
            favourite: v.favourite
          }));
        })),
        map(x => x.flat()),
        filter(x => !!x)
      );
    }, {
      itemPreview: ContentSelectorVisualPreviewComponent,
    });
  }


  export interface Data<T> {
    readonly context: SourceConfig;

    readonly icon: string;
    readonly title: string;
    
    readonly filter?: string;
    readonly disabledCategories?: ReadonlyArray<ItemCategoryId>;

    readonly selectedCategory?: ItemCategoryId;
    readonly selectedItemKey?: any;
    readonly selectedItemId?: any;

    readonly disabledItemIds?: ReadonlyArray<number>;

    readonly cancelButton?: {
      readonly icon?: string;
      readonly text?: string;
      readonly color?: string;
    };
    
    readonly acceptButton?: {
      readonly icon?: string;
      readonly text?: string;
      readonly color?: string;
    };

    readonly suggestOption?: {
      readonly icon?: string;
      readonly title?: string;
      readonly url?: string;
      readonly action?: () => void;
    }
  }

  export interface Result<T> {
    readonly filter?: string;
    readonly selectedCategory?: ItemCategoryId;
    readonly selectedItem?: T;
    readonly selectedItemKey?: any;
  }
}
